import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import { encryptId } from '../SecurityComponent/cryptoUtils';
import './trailer-card-list.scss';

function TrailerModal({ trailerKey, title, onClose }) {
    useEffect(() => {
        const onKey = e => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return ReactDOM.createPortal(
        <div className="thc-modal-backdrop" onClick={onClose}>
            <div className="thc-modal" onClick={e => e.stopPropagation()}>
                <button className="thc-modal__close" onClick={onClose}><FaTimes /></button>
                <h3 className="thc-modal__title">{title}</h3>
                <div className="thc-modal__video">
                    {trailerKey ? (
                        <iframe
                            src={`https://www.youtube.com/embed/${trailerKey}?autoplay=1&rel=0`}
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                            title={title}
                            style={{ width: '100%', height: '100%', border: 0 }}
                        />
                    ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
                            <div className="thc__spinner" />
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}

function TrailerHoverCard({ item }) {
    const navigate = useNavigate();
    const [active, setActive] = useState(false);
    const [trailerKey, setTrailerKey] = useState(null);
    const [trailerLoading, setTrailerLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const timerRef = useRef(null);
    const fetchedRef = useRef(false);

    const fetchTrailer = () => {
        if (fetchedRef.current) return;
        fetchedRef.current = true;
        setTrailerLoading(true);
        fetch(`/movies-trailer-api.php?movieId=${item.id}`)
            .then(r => r.json())
            .then(d => {
                const url = d.data?.trailerUrl;
                if (url) {
                    const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
                    if (m) setTrailerKey(m[1]);
                }
            })
            .catch(() => {})
            .finally(() => setTrailerLoading(false));
    };

    const handleEnter = () => {
        timerRef.current = setTimeout(() => {
            setActive(true);
            fetchTrailer();
        }, 900);
    };

    const handleLeave = () => {
        clearTimeout(timerRef.current);
        setActive(false);
    };

    const handleCardClick = () => {
        if (!item.episodeCount) {
            fetchTrailer();
            setModalOpen(true);
        } else {
            navigate(watchUrl);
        }
    };

    const handlePlayClick = e => {
        e.preventDefault();
        handleCardClick();
    };

    const poster   = item.posterUrl || item.imageUrl;
    const banner   = item.thumbnailUrl || item.thumbUrl || item.backdropUrl || poster;
    const watchUrl = `/watch/${item.slug}/${encryptId(1)}`;
    const score    = item.averageRating > 0 ? Number(item.averageRating).toFixed(1) : null;
    const year     = item.releaseYear || item.year || '';
    const eps      = item.episodeCount > 1 ? `${item.episodeCount} tập` : 'Phim lẻ';
    const genres   = (item.genres || []).slice(0, 3);

    return (
        <>
        {modalOpen && (
            <TrailerModal trailerKey={trailerKey} title={item.title} onClose={() => setModalOpen(false)} />
        )}
        <div
            className={`thc${active ? ' thc--on' : ''}`}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            onClick={handleCardClick}
            style={{ cursor: 'pointer' }}
        >
            <div className="thc__media">
                {active && trailerKey ? (
                    <iframe
                        className="thc__iframe"
                        src={`https://www.youtube.com/embed/${trailerKey}?autoplay=1&mute=1&controls=0&rel=0&loop=1&playlist=${trailerKey}`}
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                        title={item.title}
                    />
                ) : (
                    <img
                        src={banner} alt={item.title} className="thc__img" loading="lazy" decoding="async"
                        onError={e => { if (e.target.src !== poster) e.target.src = poster; }}
                    />
                )}
                {active && trailerLoading && (
                    <div className="thc__loading"><div className="thc__spinner" /></div>
                )}
                {!active && (
                    <div className="thc__hint"><div className="thc__play-circle">▶</div></div>
                )}
                <div className="thc__overlay" />
                {item.quality && <span className="thc__quality">{item.quality}</span>}
                {!item.episodeCount && <span className="thc__trailer-badge">Trailer</span>}
            </div>

            <div className="thc__body">
                <div className="thc__actions">
                    <Link to={watchUrl} className="thc__btn-play" onClick={handlePlayClick}>▶</Link>
                    <button className="thc__btn-round">+</button>
                    <button className="thc__btn-round">♡</button>
                </div>
                <div className="thc__meta-row">
                    {score && <span className="thc__score">★ {score}</span>}
                    <span className="thc__eps">{eps}</span>
                    {year && <span className="thc__year">{year}</span>}
                </div>
                <h3 className="thc__title">{item.title}</h3>
                {genres.length > 0 && (
                    <div className="thc__genres">
                        {genres.map((g, i) => (
                            <React.Fragment key={g.id || i}>
                                {i > 0 && <span className="thc__dot">·</span>}
                                <span>{g.name}</span>
                            </React.Fragment>
                        ))}
                    </div>
                )}
            </div>
        </div>
        </>
    );
}

const TrailerCardList = ({ genreId, type = 'new', size = 100 }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);
    const swiperRef = useRef(null);

    useEffect(() => {
        let url;
        if (genreId) {
            url = `/movies-public-api.php?type=trailer&genreId=${genreId}&page=0&size=${size}`;
        } else {
            url = `/movies-public-api.php?type=trailer&page=0&size=${size}`;
        }
        setLoading(true);
        fetch(url)
            .then(r => r.json())
            .then(d => { if (d.success) setItems(d.content || []); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [genreId, type, size]);

    const updateNav = s => { setIsBeginning(s.isBeginning); setIsEnd(s.isEnd); };
    const slidePrev = () => { const s = swiperRef.current; if (!s) return; const c = s.slidesPerViewDynamic ? s.slidesPerViewDynamic() : 1; s.slideTo(Math.max(0, s.activeIndex - c)); };
    const slideNext = () => { const s = swiperRef.current; if (!s) return; const c = s.slidesPerViewDynamic ? s.slidesPerViewDynamic() : 1; s.slideTo(s.activeIndex + c); };

    if (loading) return <div className="thc-list-loading">Đang tải...</div>;
    if (!items.length) return null;

    return (
        <div className="thc-list">
            {!isBeginning && (
                <button className="thc-list__nav thc-list__nav--prev" onClick={slidePrev}>
                    <FaChevronLeft />
                </button>
            )}
            {!isEnd && (
                <button className="thc-list__nav thc-list__nav--next" onClick={slideNext}>
                    <FaChevronRight />
                </button>
            )}
            <Swiper
                grabCursor
                spaceBetween={12}
                slidesPerView="auto"
                onSwiper={s => { swiperRef.current = s; updateNav(s); }}
                onSlideChange={updateNav}
                onReachBeginning={() => { setIsBeginning(true); setIsEnd(false); }}
                onReachEnd={() => { setIsEnd(true); setIsBeginning(false); }}
                onFromEdge={s => updateNav(s)}
            >
                {items.map(item => (
                    <SwiperSlide key={item.id} className="thc-list__slide">
                        <TrailerHoverCard item={item} />
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
};

export default TrailerCardList;
